// @ts-strict
import { modelOptions, mongoose, post, prop, type Ref } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { Organization } from "../organization/organization.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { CommandGroupFieldsWithDefaults, CommandGroupSchemaTypes } from "../types/commandGroup.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";

const SCHEMA_KEY = "CommandGroup" as const;

@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
        collection: "commandGroups",
        timestamps: true,
    },
})
@post<SchemaBase<CommandGroup>>(...addSchemaKeyHook(SCHEMA_KEY))
export class CommandGroup extends SchemaBase<CommandGroup, CommandGroupFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public name?: string;

    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false })
    public generatedFrom?: string;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ ref: () => Organization, required: true })
    public owner!: Ref<Organization>;

    @validate({ zodValidator: z.coerce.date(), responseOnly: true })
    @prop({ type: () => Date, required: true })
    public createdOn!: Date;

    static getters = {} as const satisfies Record<string, (self: CommandGroup) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<CommandGroupSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<CommandGroup, CommandGroupFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

CommandGroup.dtoValidators = getZodSchemaFromClass(
    CommandGroup,
) as unknown as DtoValidatorTypes<CommandGroupSchemaTypes>;
